import './styles.scss'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { LoadingOutlined } from '@ant-design/icons'
import { Avatar, Col, Row, Tooltip } from 'antd'

import AddQuickEntry from '@/components/AddQuickEntry'
import B2becTranslation from '@/components/B2becTranslation'
import B2becUpload from '@/components/B2becUpload'
import CustomButton from '@/components/CustomButton'
import { ChevronLeftIcon } from '@/components/Icons'
import { NotificationManager } from '@/components/Notification'
import B2becButton from 'components/B2becButton'
import { cartService, reportService } from '@/services'
import { useAppDispatch, useAppSelector } from '@/store/hook'
import {
  selectCartItems,
  selectIsAvoidProceedingCart,
  selectIsEmptyingCart,
  selectIsFetchingCart,
} from '@/store/selectors/cartSelector'
import {
  selectAllowDirectShopCheckout,
  selectDeactivateShoppingCart,
  selectHideQuickEntry,
  selectHideVoucher,
} from '@/store/selectors/configSelector'
import { selectHasSessionBasedShoppingCart } from '@/store/selectors/userSelector'
import { trackLinkName } from '@/store/slices/adobeAnalyticSlice'
import {
  addEmptyRow,
  addProductToCart,
  emptyCart,
  getCartItems,
  removeProductAddedFromQuickEntry,
} from '@/store/slices/cartSlice'
import { useDeviceDetect } from '@/hooks'
import useAdobeAnalysis from '@/hooks/useAdobeAnalysis'
import useAsync from '@/hooks/useAsync'
import useBreadcrumbs from '@/hooks/useBreadcrumbs'
import useDocumentTitle from '@/hooks/useDocumentTitle'
import usePermission from '@/hooks/usePermission'
import { usePunchoutContext } from '@/contexts/punchout/usePunchoutContext'
import PermissionWrapper from '@/HOCs/permissionWrapper'
import { ACCEPTED_UPLOAD_FILE_EXTENSIONS, ASYNC_STATUS } from '@/libs/constants'
import { LINK_NAMES, PAGE_TYPES, TRACK_DATA } from '@/libs/constants/adobeAnalytics'
import {
  MODULE_CART_CHECKOUT_RIGHTS,
  MODULE_SHOP_NAVIGATION_RIGHTS,
} from '@/libs/constants/modulerights'
import { analyzeLinkName } from '@/libs/utils/adobeAnalysis'
import { isEmptyArray, isValidArray } from '@/libs/utils/array'
import { linkGenerator } from '@/libs/utils/language'
import { downloadPDF } from '@/libs/utils/PDFHandler'
import { isStringNotEmpty } from '@/libs/utils/stringUltis'
import AddProductIcon from '@/assets/icons/addproduct.svg?react'
import InformationIcon from '@/assets/icons/information.svg?react'

import CartItemTable from './CartItemTable'
import EmptyCartConfirmModal from './EmptyCartConfirmModal'
import CartMobile from './Mobile'
import RedeemVoucher from './RedeemVoucher'

const handleExportCartError = () =>
  NotificationManager.error({
    message: 'notification.error.summary.cart',
  })

const handleExportCartSuccess = (response) => {
  if (response?.status === 200 && response?.data) {
    if (
      isStringNotEmpty(response.data.file)
      && isStringNotEmpty(response.data.fileName)
    ) {
      downloadPDF(response.data)
    } else {
      handleExportCartError()
    }
  } else {
    handleExportCartError()
  }
}

const CartContent = ({ breadcrumbs }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const [isEmptyCartModalVisible, setIsEmptyCartModalVisible] = useState(false)
  const { sessionId, isPreparingCXMLData, onSubmitPunchoutData } = usePunchoutContext() || {}
  const [isUploading, setIsUploading] = useState(false)
  const isShoppingCartDeactivated = useAppSelector(
    selectDeactivateShoppingCart,
  )

  useAdobeAnalysis(TRACK_DATA.CART, {
    pageName: 'cart',
    pageId: '2',
    pageType: PAGE_TYPES.CART,
  })

  useDocumentTitle('pageTitle.shoppingCart')
  useBreadcrumbs(breadcrumbs)

  const { isMobile } = useDeviceDetect()

  const quickEntryRef = useRef(null)

  const isLoading = useAppSelector(selectIsFetchingCart)
  const cartItems = useAppSelector(selectCartItems)
  const isAvoidProceedingCart = useAppSelector(selectIsAvoidProceedingCart)
  const isEmptyingCart = useAppSelector(selectIsEmptyingCart)
  const hasSessionBasedShoppingCart = useAppSelector(
    selectHasSessionBasedShoppingCart,
  )
  const allowDirectShopCheckout = useAppSelector(selectAllowDirectShopCheckout)
  const shouldTransferCartToProcurement =
    hasSessionBasedShoppingCart && !allowDirectShopCheckout
  const hideQuickEntryConfig = useAppSelector(selectHideQuickEntry)
  const hideVoucher = useAppSelector(selectHideVoucher)
  const shouldShowQuickEntry = !hideQuickEntryConfig && !isLoading
  const { hasPermission: canShowCheckoutButton } = usePermission(
    MODULE_CART_CHECKOUT_RIGHTS.SHOW_CHECKOUT_BUTTON,
  )

  const shouldHideCheckoutButton = isLoading || isShoppingCartDeactivated || !canShowCheckoutButton

  const { execute: executeExportCart, status: exportCartStatus } = useAsync(
    async (ssId) => reportService.exportShoppingCart({ sessionId: ssId }),
    false,
    handleExportCartSuccess,
    handleExportCartError,
  )

  const isAddButtonDisabled = useMemo(() => {
    return (
      isValidArray(cartItems)
      && cartItems.some((item) => item.emptyRow === true)
    )
  }, [cartItems])

  const onHandleExportCart = useCallback(() => {
    dispatch(trackLinkName(analyzeLinkName(LINK_NAMES.PDF_EXPORT)))

    executeExportCart(hasSessionBasedShoppingCart ? sessionId : undefined)
  }, [executeExportCart, hasSessionBasedShoppingCart, sessionId, dispatch])

  const handleQuickAddItem = (materialNumber) => {
    dispatch(addProductToCart({ materialNumber }))
  }

  const scrollToRef = (ref: React.RefObject<HTMLDivElement>) => {
    if (ref.current) {
      window.scrollTo(0, ref.current.offsetTop - 800)
    }
  }

  const showAddQuickEntry = () => {
    const searchRow = {
      id: '',
      materialNumber: (
        <AddQuickEntry
          onAddItem={handleQuickAddItem}
          currentItems={cartItems}
          duplicateWarningContent={t('cart.duplicate')}
        />
      ),
      imgUrl: (
        <Avatar
          className="cart__image"
          shape="square"
          size={{ xs: 64, md: 80 }}
          src={AddProductIcon}
        />
      ),
      price: 0,
      quantity: 0,
      emptyRow: true,
    }

    dispatch(addEmptyRow(searchRow))
    scrollToRef(quickEntryRef)
  }

  const proceedToCheckoutHandler = useCallback(() => {
    try {
      if (isEmptyArray(cartItems)) {
        NotificationManager.error({
          message: 'notification.error.proceedCheckout',
          description: 'notification.error.cartListIsEmpty',
        })
        return
      }
      // check cart has invalid items
      if ((cartItems || []).some((item) => !!item?.error)) {
        NotificationManager.error({
          message: 'notification.error.proceedCheckout',
          description: 'notification.error.nonexistentNumberInCart',
        })
        return
      }

      if (
        shouldTransferCartToProcurement
        && typeof onSubmitPunchoutData === 'function'
      ) {
        onSubmitPunchoutData()
      } else {
        navigate(linkGenerator('/checkout'))
      }
    } catch (error) {
      console.error('error:', error.message)
    }
  }, [navigate, cartItems, shouldTransferCartToProcurement, onSubmitPunchoutData])

  const handleEmptyCart = () => {
    dispatch(emptyCart())
    setIsEmptyCartModalVisible(false)
  }

  const handleUploadProductsToCart = async ({ file }) => {
    const isCSV = ACCEPTED_UPLOAD_FILE_EXTENSIONS?.includes(file?.type)

    if (!isCSV) {
      NotificationManager.error({
        message: 'notification.upload.csvOnly',
      })

      return
    }

    setIsUploading(true)

    const formData = new FormData()
    formData.append('FormFile', file)

    try {
      const uploadResponse = await cartService.uploadProductsToCart({
        formData,
        sessionId: hasSessionBasedShoppingCart ? sessionId : undefined,
      })

      if (uploadResponse?.status === 200) {
        dispatch(getCartItems())
        NotificationManager.success({
          message: 'notification.success.upload',
        })
      }
    } catch (error) {
      console.error('Upload error:', error?.message)
      NotificationManager.error({
        message: 'notification.error.upload',
      })
    } finally {
      setIsUploading(false)
    }
  }

  useEffect(() => {
    dispatch(getCartItems())
  }, [dispatch])

  useEffect(
    () => () => {
      dispatch(removeProductAddedFromQuickEntry())
    },
    [dispatch],
  )

  return isMobile
    ? (
      <CartMobile
        isAddBtnDisabled={isAddButtonDisabled}
        isLoading={isLoading}
        isShowQuickEntry={shouldShowQuickEntry}
        proceedToCheckoutHandler={proceedToCheckoutHandler}
        showAddQuickEntry={showAddQuickEntry}
        shouldTransferCartToProcurement={shouldTransferCartToProcurement}
        isPreparingCXMLData={isPreparingCXMLData}
      />
    )
    : (
      <div className="cart-wrapper">
        <div className="cart-heading">
          <h1>{t('cart.header')}</h1>
          <div className="cart-heading__buttons mb-4">
            <div className="leftSection">
              {shouldShowQuickEntry && (
                <PermissionWrapper
                  permission={MODULE_SHOP_NAVIGATION_RIGHTS.ADD_PRODUCT_WITH_QUICK_ENTRY}
                >
                  <CustomButton
                    className="cart-heading__add mr-4"
                    disabled={isAddButtonDisabled || isAvoidProceedingCart}
                    type="ghost"
                    onClick={showAddQuickEntry}
                  >
                    {t('cart.addQuickEntryBtn')}
                  </CustomButton>
                </PermissionWrapper>
              )}
              <PermissionWrapper
                permission={MODULE_SHOP_NAVIGATION_RIGHTS.EMPTY_CART}
              >
                <CustomButton
                  disabled={isEmptyingCart || isAddButtonDisabled}
                  type="ghost"
                  onClick={() => setIsEmptyCartModalVisible(true)}
                >
                  {t('cart.modal.emptyCartBtn')}
                </CustomButton>
              </PermissionWrapper>
            </div>
            <div className="rightSection">
              <div>
                <Tooltip
                  title={
                    <B2becTranslation
                      isComponent
                      value="cart.uploadCart"
                      htmlTags={[
                        <a
                          key="upload_sample"
                          aria-label="Download sample file"
                          download="sample_file.csv"
                          href="/items.csv"
                          rel="noreferrer noopener"
                          target="_blank"
                        >
                          csv files
                        </a>,
                      ]}
                    />
                  }
                >
                  <InformationIcon height={24} width={24} />
                </Tooltip>
              </div>
              <B2becUpload
                customRequest={handleUploadProductsToCart}
                isUploading={isUploading}
                text={t('cart.buttons.uploadCsvFile')}
              />
            </div>
          </div>
        </div>

        <CartItemTable isLoading={isLoading} />

        {isEmptyCartModalVisible && (
          <EmptyCartConfirmModal
            isEmptyCartModalVisible={isEmptyCartModalVisible}
            onCancel={() => setIsEmptyCartModalVisible(false)}
            onEmptyCart={handleEmptyCart}
          />
        )}

        <div ref={quickEntryRef} className="cart-bottom">
          <div className="cart-bottom__action">
            <div className="cart-bottom__action__link" style={{ marginTop: 10 }}>
              <CustomButton
                className="cart-bottom__action__link--with-icon"
                type="ghost"
                onClick={async () => navigate(linkGenerator('/my-workspace'))}
              >
                <ChevronLeftIcon />
                {t('cart.continueShopping')}
              </CustomButton>
            </div>
            {shouldHideCheckoutButton ? null : (
              <div style={{ marginTop: 10 }}>
                <B2becButton
                  disabled={isAvoidProceedingCart || isPreparingCXMLData}
                  loading={isPreparingCXMLData}
                  onClick={proceedToCheckoutHandler}
                >
                  {shouldTransferCartToProcurement
                    ? t('cart.proceedToPurchaseSystem')
                    : t('cart.proceedToCheckout')}
                </B2becButton>
              </div>
            )}
          </div>
          <PermissionWrapper
            permission={MODULE_SHOP_NAVIGATION_RIGHTS.CART_EXPORT}
          >
            <Row gutter={8} style={{ marginTop: '48px' }}>
              <Col>
                <CustomButton
                  type="ghost"
                  buttonSize="small"
                  disabled={exportCartStatus === ASYNC_STATUS.PENDING
                    || isAvoidProceedingCart}
                  onClick={onHandleExportCart}
                >
                  {exportCartStatus === ASYNC_STATUS.PENDING && (
                    <LoadingOutlined style={{ marginRight: 8 }} />
                  )}
                  {t('cart.buttons.exportPDF')}
                </CustomButton>
              </Col>
            </Row>
          </PermissionWrapper>

          {!hideVoucher && (
            <PermissionWrapper
              permission={MODULE_CART_CHECKOUT_RIGHTS.ADD_VOUCHER_TO_CART}
            >
              <RedeemVoucher isDisabled={isAvoidProceedingCart} />
            </PermissionWrapper>
          )}
        </div>
      </div>
    )
}

const Cart = ({ breadcrumbs }) => {
  return <CartContent breadcrumbs={breadcrumbs} />
}

export default Cart
